// ═══════════════════════════════════════════════════════════════
// trustack.js — verticalstack Trustack rating algorithm v1
// Path: src/components/trustack.js
//
// S8.D (June 29, 2026) — NEW FILE. Pure scoring module. No Firestore,
// no network — input is a vendor object, output is numbers. Lives in
// src/components/ alongside processConfigs.js per repo convention for
// pure helpers.
//
// AS-S8-TRUSTACK-CLIENT-V1 (LOCKED, revisit S13)
//   Ships client-side. The FORMULA, WEIGHTS, TIER-ELIGIBILITY GATES, and
//   90-DAY DECAY CURVE are locked here. At S13 — when reviews (S9+),
//   Twilio responsiveness, and completed work orders (workOrders, S13)
//   light up the input streams — this same logic lifts into a
//   computeTrustackScore Cloud Function that computes-once-and-stores and
//   runs decay on a nightly schedule. The CF becomes a thin wrapper around
//   this validated v1 module. Until then it runs in-browser and degrades
//   cleanly: a vendor with no live signals returns hasLiveSignals=false so
//   the UI can honestly show "—" instead of a fabricated number.
//
// THE FOUR SIGNALS (weights sum to 100)
//   external       30 — public reputation: BBB · Google · Yelp (0–5 each).
//                       Reputation the vendor brings WITH them. Lights up
//                       when review ingestion wires (S9+).
//   responsiveness 25 — Twilio average reply time. Reputation earned in
//                       conversation. Lights up as messaging accrues.
//   completion     30 — job-completion + first-fix rate from Marketplace
//                       fires. Reputation earned doing work. Lights up
//                       when workOrders ships (S13).
//   recency        15 — freshness modifier. 90-day linear decay; past 90
//                       days the vendor is flagged Inactive.
//
//   external + completion tie at the top (what the public thinks AND
//   whether they finish the work matter most + equally). Responsiveness
//   next. Recency lowest — it is a freshness modifier, not a quality
//   measure.
//
// WEIGHT RENORMALIZATION
//   The blend runs over PRESENT signals only, renormalizing the weight
//   denominator to the present subset. A vendor with only external
//   reviews still scores honestly from that stream (effective weight
//   100%), rather than being dragged toward zero by absent streams.
//
// EXPECTED RAW INPUT SHAPE (vendor.signals — absent on all docs today)
//   {
//     external:       { bbb?: 0-5, google?: 0-5, yelp?: 0-5 },
//     responsiveness: { avgResponseHours?: number },
//     completion:     { completionRate?: 0-100, firstFixRate?: 0-100 },
//     recency:        { lastActivityAt?: epochMs | ISO string }
//   }
//   No vendor doc carries `signals` yet, so every live vendor returns
//   hasLiveSignals=false. Harness mock vendors (Network.jsx MOCK_VENDORS)
//   carry synthetic signals so the computed path is visible in
//   ?xcnet-smoke=1.
// ═══════════════════════════════════════════════════════════════

export const TRUSTACK_WEIGHTS = Object.freeze({
  external: 30,
  responsiveness: 25,
  completion: 30,
  recency: 15,
});

export const DECAY_DAYS = 90;

// ── small math helpers ───────────────────────────────────────────────────
function clamp(n, lo = 0, hi = 100) {
  return Math.max(lo, Math.min(hi, n));
}

function daysSince(when) {
  if (when == null) return null;
  const t = typeof when === 'number' ? when : Date.parse(when);
  if (Number.isNaN(t)) return null;
  return Math.max(0, (Date.now() - t) / 86400000);
}

// ── per-signal normalizers → 0..100, or null when the stream is absent ───

// External public reputation: average of available BBB / Google / Yelp
// star ratings (each 0–5), scaled to 0–100.
function externalScore(signals) {
  const ext = signals && signals.external;
  if (!ext) return null;
  const stars = ['bbb', 'google', 'yelp']
    .map((k) => ext[k])
    .filter((v) => typeof v === 'number');
  if (stars.length === 0) return null;
  const avg5 = stars.reduce((a, b) => a + b, 0) / stars.length;
  return Math.round(clamp((avg5 / 5) * 100));
}

// Responsiveness: faster is better. <=1h → 100, >=48h → 0, linear between.
function responsivenessScore(signals) {
  const r = signals && signals.responsiveness;
  const hrs = r && r.avgResponseHours;
  if (typeof hrs !== 'number') return null;
  if (hrs <= 1) return 100;
  if (hrs >= 48) return 0;
  return Math.round(clamp(100 * (1 - (hrs - 1) / (48 - 1))));
}

// Completion: blend job-completion rate (60%) with first-fix rate (40%).
// First-fix is optional; when absent, completion rate stands alone.
function completionScore(signals) {
  const c = signals && signals.completion;
  if (!c || typeof c.completionRate !== 'number') return null;
  const comp = clamp(c.completionRate);
  if (typeof c.firstFixRate !== 'number') return Math.round(comp);
  const ff = clamp(c.firstFixRate);
  return Math.round(0.6 * comp + 0.4 * ff);
}

// Recency: 90-day linear decay. 0 days → 100, 90+ days → 0 (Inactive).
function recencyScore(signals) {
  const rec = signals && signals.recency;
  const last = rec && rec.lastActivityAt;
  if (last == null) return null;
  const d = daysSince(last);
  if (d == null) return null;
  if (d >= DECAY_DAYS) return 0;
  return Math.round(clamp(100 * (1 - d / DECAY_DAYS)));
}

// ── tier-eligibility (ADVISORY) ──────────────────────────────────────────
// Surfaces the tier the score + compliance gates would support. Advisory
// only — does NOT mutate vendor.tier (tier is a superAdmin write per rules
// v3.4). Returns null when no score can be computed.
function computeTierEligibility(score, vendor) {
  if (score == null) return null;
  const hasCompliance = !!(vendor && vendor.insurance) && !!(vendor && vendor.w9OnFile);
  const jobs = (vendor && vendor.jobs) || 0;
  if (score >= 80 && hasCompliance && jobs >= 3) return 'T1';
  if (score >= 60) return 'T2';
  return 'T3';
}

// ── main entry ────────────────────────────────────────────────────────────
// computeTrustack(vendor) →
//   {
//     score:          0-100 | null,        // weighted blend, or null if no signals
//     subScores:      { external, responsiveness, completion, recency },  // each 0-100 | null
//     hasLiveSignals: boolean,             // false → UI shows "—"
//     inactive:       boolean,             // recency present AND >90d stale
//     tierEligible:   'T1' | 'T2' | 'T3' | null,  // advisory
//     weights:        TRUSTACK_WEIGHTS,
//   }
export function computeTrustack(vendor) {
  const signals = (vendor && vendor.signals) || null;

  const subScores = {
    external: externalScore(signals),
    responsiveness: responsivenessScore(signals),
    completion: completionScore(signals),
    recency: recencyScore(signals),
  };

  const present = Object.entries(subScores).filter(([, v]) => v !== null);
  const hasLiveSignals = present.length > 0;

  let score = null;
  if (hasLiveSignals) {
    let wsum = 0;
    let weighted = 0;
    for (const [k, v] of present) {
      const w = TRUSTACK_WEIGHTS[k];
      wsum += w;
      weighted += v * w;
    }
    score = wsum > 0 ? Math.round(weighted / wsum) : null;
  }

  const d = signals && signals.recency ? daysSince(signals.recency.lastActivityAt) : null;
  const inactive = d != null && d >= DECAY_DAYS;

  return {
    score,
    subScores,
    hasLiveSignals,
    inactive,
    tierEligible: computeTierEligibility(score, vendor),
    weights: TRUSTACK_WEIGHTS,
  };
}

export default computeTrustack;
